import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from './http.service';
import { API_PATHS, HTTP_METHODS } from '../common/constants/common.constants';
import { marketAPIFilterInput } from '../common/interface/common.interface';
import { API_INPUT, COMMENT_POST_INPUT, NONCE_REQUEST_INPUT, SIGNATURE_INPUT, UPDATE_USEREMAIL_INPUT, UPDATE_USERNAME_INPUT, VERIFY_SIGNATURE_INPUT } from '../common/interface/request.interface';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { EXECUTE_SAFE_TRANSACTION_INPUT } from '../common/interface/request.interface';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(private service: HttpService,
    private http: HttpClient
  ) { }

  getCategoriesList(): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.GET_CATEGORIES,
      method: HTTP_METHODS.GET,
      params: { status: "ACTIVE" }
    }

    return this.service.invoke(obj);
  }

  getMarketsList(filters: marketAPIFilterInput): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.GET_MARKTEDATA,
      method: HTTP_METHODS.GET,
      params: filters
    };

    return this.service.invoke(obj);
  }

  getMarketDetail(market_id: string | number): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.GET_MARKTEDATA + "/" + market_id,
      method: HTTP_METHODS.GET,
    }

    return this.service.invoke(obj);
  }

  getMarketComments(params: COMMENT_POST_INPUT): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.GET_MARKET_COMMENT,
      method: HTTP_METHODS.GET,
      params
    }

    return this.service.invoke(obj);
  }

  postMarketComment(data: COMMENT_POST_INPUT): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.POST_MARKET_COMMENT,
      method: HTTP_METHODS.POST,
      data
    }

    return this.service.invoke(obj);
  }

  authenticateWeb2User(): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.AUTHETNICATE_USERS,
      method: HTTP_METHODS.POST,
    };

    return this.service.invoke(obj);
  }

  requestForNonce(data: NONCE_REQUEST_INPUT): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.REQUEST_FOR_NONCE_BY_ADDRESS,
      method: HTTP_METHODS.POST,
      data
    };

    return this.service.invoke(obj);
  }

  verifySignature(data: VERIFY_SIGNATURE_INPUT): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.VERIFY_SIGNATURE,
      method: HTTP_METHODS.POST,
      data
    };

    return this.service.invoke(obj);
  }

  getUserProfile(user_id: string | number): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.GET_USER_DETAILS,
      method: HTTP_METHODS.GET,
      params: { user_id }
    };

    return this.service.invoke(obj);
  }

  getUserTradingStats(user_id: string): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.GET_USER_TRADING_STATS.replace('{user_id}', user_id),
      method: HTTP_METHODS.GET,
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  updateProfile(data: any, params: any): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.UPDATE_USER_DETAILS,
      method: HTTP_METHODS.PATCH,
      data,
      params
    };

    return this.service.invoke(obj);
  }

  getProfileImageURL(data: FormData): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.UPLOAD_PROFILE_IMAGE,
      method: HTTP_METHODS.POST,
      data
    };

    return this.service.invoke(obj);
  }

  updateUserName(data: UPDATE_USERNAME_INPUT, user_id: string | number): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.UPDAET_USER_NAME,
      method: HTTP_METHODS.PATCH,
      data,
      params: { user_id }
    };

    return this.service.invoke(obj);
  }

  validateUsername(user_name: string): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.VALIDATE_USER_NAME,
      method: HTTP_METHODS.GET,
      params: { user_name }
    };

    return this.service.invoke(obj);
  }

  updateEmailAddress(data: UPDATE_USEREMAIL_INPUT, user_id: string | number): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.UPDATE_EMAIL_ADDRESS,
      method: HTTP_METHODS.POST,
      data,
      params: { user_id }
    }

    return this.service.invoke(obj);
  }

  verifyEmailToken(token: string): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.VERIFY_EMAIL_TOKEN,
      method: HTTP_METHODS.GET,
      params: { token }
    };

    return this.service.invoke(obj);
  }

  enableTrading(signatureParams: SIGNATURE_INPUT): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.ENABLE_USER_TRADING,
      method: HTTP_METHODS.POST,
      data: signatureParams
    };

    return this.service.invoke(obj);
  }

  enable2FA(): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.ENABLE_2FA,
      method: HTTP_METHODS.POST,
      params: {
        "require_2fa_token": false
      }
    };

    return this.service.invoke(obj);
  }

  verify2FA(otp: string, isTfaToken: boolean): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.VERIFY_2FA,
      method: HTTP_METHODS.POST,
      data: { otp },
      headers: {
        isTfaToken: isTfaToken ? 'true' : 'false'
      }
    };

    return this.service.invoke(obj);
  }

  remove2FA(code: string): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.REMOVE_2FA,
      method: HTTP_METHODS.POST,
      params: {
        "require_2fa_token": true
      },
      data: {
        "two_factor_recovery_code": code
      }
    }

    return this.service.invoke(obj);
  }

  disable2FA(code: string): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.DISABLE_2FA,
      method: HTTP_METHODS.POST,
      data: {
        "otp": code
      }
    };

    return this.service.invoke(obj);
  }

  updateEmailAlertPreference(enable_email_alerts: boolean): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.UPDATE_EMAIL_ALERTS,
      method: HTTP_METHODS.PATCH,
      params: {
        enable_email_alerts: enable_email_alerts
      }
    };

    return this.service.invoke(obj);
  }

  logout(): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.USER_LOGOUT,
      method: HTTP_METHODS.POST
    };

    return this.service.invoke(obj);
  }

  placeOrder(orderPayload: any, marketId: string, userId: string): Observable<any> {
    const safePayload = JSON.parse(JSON.stringify(orderPayload, (_k, v) =>
      typeof v === 'bigint' ? v.toString() : v
    ));

    let obj: API_INPUT = {
      url: API_PATHS.PLACE_ORDER.replace('{market_id}', marketId),
      method: HTTP_METHODS.POST,
      data: safePayload,
      params: { user_id: userId },
      useTradingEngine: true
    };

    return this.service.invoke(obj);
  }

  getTopHolders(marketId: string, page: number = 1, limit: number = 25): Observable<any> {
    const params = { page, limit };
    let obj: API_INPUT = {
      url: API_PATHS.GET_TOP_HOLDERS.replace('{market_id}', marketId),
      method: HTTP_METHODS.GET,
      params: params,
      useTradingEngine: true
    };

    return this.service.invoke(obj);
  }

  getMarketActivity(marketId: string | null, userId?: string | null, page: number = 1, limit: number = 25, minAmount?: number | null): Observable<any> {
    const params: any = { page, limit };
    if (marketId) params.market_id = marketId;
    if (userId) params.user_id = userId;
    if (minAmount) params.min_amount = minAmount;

    let obj: API_INPUT = {
      url: API_PATHS.GET_MARKET_ACTIVITY,
      method: HTTP_METHODS.GET,
      params: params,
      useTradingEngine: true
    };

    return this.service.invoke(obj);
  }

  getMarketPositions(
    marketId: string,
    status?: string,
    sort?: string,
    page: number = 1,
    pageSize: number = 25
  ): Observable<any> {
    const params: any = { page, page_size: pageSize };
    if (status) params.status = status;
    if (sort) params.sort = sort;

    let obj: API_INPUT = {
      url: API_PATHS.GET_MARKET_POSITIONS.replace('{market_id}', marketId),
      method: HTTP_METHODS.GET,
      params: params,
      useTradingEngine: true
    };

    return this.service.invoke(obj);
  }

  getOrderBook(marketId: string, outcomeId: string) {
    let obj: API_INPUT = {
      url: API_PATHS.GET_MARKET_ORDERBOOK.replace('{market_id}', marketId),
      method: HTTP_METHODS.GET,
      params: { outcome_id: outcomeId },
      useTradingEngine: true
    };

    return this.service.invoke(obj);
  }

  patchTimeInForce(time_in_force: string): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.TIME_IN_FORCE,
      method: HTTP_METHODS.PATCH,
      data: { time_in_force },
      params: { time_in_force }
    };

    return this.service.invoke(obj);
  }

  getMarketPriceHistory(marketId: string, outcomeId: string, intervalType: string): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.GET_PRICE_HISTORY.replace('{market_id}', marketId), // adding leading slash since it was 'users/markets/...' in constants
      method: HTTP_METHODS.GET,
      params: {
        outcome_id: outcomeId,
        interval_type: intervalType
      },
      useTradingEngine: true
    };

    return this.service.invoke(obj);
  }


  depositFunds(userId: string, amount: number, txn_hash?: string): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.DEPOSIT_WALLET,
      method: HTTP_METHODS.POST,
      params: { user_id: userId },
      data: { amount, txn_hash },
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  executePreApproval(userId: string, data: EXECUTE_SAFE_TRANSACTION_INPUT): Observable<any> {
    console.log("ApiService: Initiating executePreApproval for user:", userId);
    // Ensure BigInt (like nonce or amount) is converted to string for JSON serialization
    const safePayload = JSON.parse(JSON.stringify(data, (_k, v) =>
      typeof v === 'bigint' ? v.toString() : v
    ));
    console.log("ApiService: Pre-approval Payload:", safePayload);

    let obj: API_INPUT = {
      url: API_PATHS.PRE_APPROVAL_EXECUTE,
      method: HTTP_METHODS.POST,
      params: { user_id: userId },
      data: safePayload,
      useTradingEngine: true
    };

    return this.service.invoke(obj);
  }

  getMarketOpenOrders(marketId: string | null, userId: string, search?: string | null, lifecycle: string = 'OPEN', page: number = 1, limit: number = 100): Observable<any> {
    let params: any = { user_id: userId, lifecycle, page, limit, page_size: limit };
    if (marketId) {
      params.market_id = marketId;
    }
    if (search) {
      params.search = search;
    }
    let obj: API_INPUT = {
      url: API_PATHS.GET_MARKET_OPEN_ORDERS,
      method: HTTP_METHODS.GET,
      params,
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  getUserOrderHistory(userId: string, search?: string | null, fromDate?: string | null, toDate?: string | null, orderType: string = 'ALL', orderDirection: string = 'NEWEST', page: number = 1, limit: number = 25): Observable<any> {
    let params: any = { user_id: userId, page, limit, order_type: orderType, order_direction: orderDirection };
    if (search) {
      params.search = search;
    }
    if (fromDate) {
      params.from_date = fromDate;
    }
    if (toDate) {
      params.to_date = toDate;
    }
    let obj: API_INPUT = {
      url: API_PATHS.GET_USER_ORDER_HISTORY,
      method: HTTP_METHODS.GET,
      params,
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  exportUserOrderHistory(userId: string, search?: string | null, fromDate?: string | null, toDate?: string | null, orderType: string = 'ALL', orderDirection: string = 'NEWEST'): Observable<Blob> {
    let params: any = { user_id: userId, order_type: orderType, order_direction: orderDirection };
    if (search) {
      params.search = search;
    }
    if (fromDate) {
      params.from_date = fromDate;
    }
    if (toDate) {
      params.to_date = toDate;
    }
    const url = `${environment.TRADING_ENGINE_URL}${API_PATHS.EXPORT_USER_ORDER_HISTORY}`;
    return this.http.get(url, {
      params,
      responseType: 'blob',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      }
    });
  }

  getUserProfitSnapshot(userId: string): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.GET_USER_PROFIT_SNAPSHOT,
      method: HTTP_METHODS.GET,
      params: { user_id: userId },
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  getUserProfitHistory(userId: string, interval: string): Observable<any> {
    let obj: API_INPUT = {
      url: API_PATHS.GET_USER_PROFIT_HISTORY,
      method: HTTP_METHODS.GET,
      params: { user_id: userId, interval },
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  getUserPositions(userId: string, search?: string | null, sortBy?: string | null, page: number = 1, limit: number = 100, status: string = 'ALL'): Observable<any> {
    let params: any = { user_id: userId, page, limit, page_size: limit, status };
    if (search) {
      params.search = search;
    }
    if (sortBy) {
      params.sort_by = sortBy;
    }
    let obj: API_INPUT = {
      url: API_PATHS.GET_USER_POSITIONS,
      method: HTTP_METHODS.GET,
      params,
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  // inside ApiService class
  prepareRedeem(marketId: string, userId: string, outcomeId?: string): Observable<any> {
    const params: any = { user_id: userId };
    if (outcomeId) params.outcome_id = outcomeId;
    const obj: API_INPUT = {
      url: API_PATHS.PREPARE_REDEEM.replace('{market_id}', marketId),
      method: HTTP_METHODS.POST,
      params,
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  executeRedeem(marketId: string, userId: string, payload: any, outcomeId?: string): Observable<any> {
    const params: any = { user_id: userId };
    if (outcomeId) params.outcome_id = outcomeId;
    const obj: API_INPUT = {
      url: API_PATHS.EXECUTE_REDEEM.replace('{market_id}', marketId),
      method: HTTP_METHODS.POST,
      params,
      data: payload,
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  getTransakSession(userId: string): Observable<any> {
    const obj: API_INPUT = {
      url: API_PATHS.TRANSAK_SESSION,
      method: HTTP_METHODS.GET,
      params: { user_id: userId },
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  streamUserDeposits(userId: string): Observable<any> {
    const url = `${environment.TRADING_ENGINE_URL}transak/deposit/stream/${userId}`;
    return new Observable(observer => {
      const eventSource = new EventSource(url);

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          observer.next(data);
        } catch (e) {
          observer.next(event.data);
        }
      };

      eventSource.onerror = (error) => {
        observer.error(error);
        eventSource.close();
      };

      return () => eventSource.close();
    });
  }

  toggleBookmark(marketId: string): Observable<any> {
    const obj: API_INPUT = {
      url: API_PATHS.TOGGLE_MARKET_BOOKMARK.replace('{market_id}', marketId),
      method: HTTP_METHODS.POST,
    };
    return this.service.invoke(obj);
  }

  getLeaderboard(params: any): Observable<any> {
    const obj: API_INPUT = {
      url: API_PATHS.GET_LEADERBOARD,
      method: HTTP_METHODS.GET,
      params,
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  getLeaderboardTopWins(): Observable<any> {
    const obj: API_INPUT = {
      url: API_PATHS.GET_LEADERBOARD_TOP_WINS,
      method: HTTP_METHODS.GET,
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  getMarketContext(marketId: string): Observable<any> {
    const obj: API_INPUT = {
      url: API_PATHS.GET_MARKET_CONTEXT.replace('{market_id}', marketId),
      method: HTTP_METHODS.GET,
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  generateMarketContext(marketId: string): Observable<any> {
    const obj: API_INPUT = {
      url: API_PATHS.GENERATE_MARKET_CONTEXT.replace('{market_id}', marketId),
      method: HTTP_METHODS.POST,
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  prepareWithdraw(userId: string, payload: { amount: number; external_wallet_address?: string }): Observable<any> {
    const obj: API_INPUT = {
      url: API_PATHS.WITHDRAW_PREPARE,
      method: HTTP_METHODS.POST,
      params: { user_id: userId },
      data: payload,
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }
  executeWithdraw(userId: string, payload: any): Observable<any> {
    const obj: API_INPUT = {
      url: API_PATHS.WITHDRAW_EXECUTE,
      method: HTTP_METHODS.POST,
      params: { user_id: userId },
      data: payload,
      useTradingEngine: true
    };
    return this.service.invoke(obj);
  }

  streamPortfolioSummary(userId: string): Observable<any> {
    const url = `${environment.TRADING_ENGINE_URL}${API_PATHS.STREAM_PORTFOLIO_SUMMARY.slice(1)}?user_id=${userId}`;
    return new Observable(observer => {
      const eventSource = new EventSource(url);

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          observer.next(data);
        } catch (e) {
          observer.next(event.data);
        }
      };

      eventSource.onerror = (error) => {
        observer.error(error);
        eventSource.close();
      };

      return () => eventSource.close();
    });
  }

  streamMarketOrderbook(marketId: string, outcomeId: string): Observable<any> {
    const url = `${environment.TRADING_ENGINE_URL}markets/stream/${marketId}?outcome_id=${outcomeId}`;
    return new Observable(observer => {
      const eventSource = new EventSource(url);

      const handler = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          observer.next(data);
        } catch (e) {
          observer.next(event.data);
        }
      };

      eventSource.addEventListener('snapshot', handler);
      eventSource.onmessage = handler;

      eventSource.onerror = (error) => {
        observer.error(error);
        eventSource.close();
      };

      return () => {
        eventSource.removeEventListener('snapshot', handler);
        eventSource.close();
      };
    });
  }

  streamOutcomePosition(outcomeId: string, userId: string): Observable<any> {
    const url = `${environment.TRADING_ENGINE_URL}users/markets/outcomes/${outcomeId}/position/stream?user_id=${userId}`;
    return new Observable(observer => {
      const eventSource = new EventSource(url);

      const handler = (event: MessageEvent) => {
        try {
          // Resilient parsing: check if event.data is already an object or needs parsing
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          observer.next(data);
        } catch (e) {
          console.warn("SSE JSON Parse Error:", e, event.data);
          observer.next(event.data);
        }
      };

      eventSource.onopen = (event) => {
        // console.log("SSE Stream Connection Opened successfully.");
      };

      // Listen for named 'update' event and default 'message' event
      eventSource.addEventListener('update', handler);
      eventSource.onmessage = handler;

      eventSource.onerror = (error) => {
        console.error("SSE Stream Connection Error:", error);
        observer.error(error);
        eventSource.close();
      };

      return () => {
        eventSource.removeEventListener('update', handler);
        eventSource.close();
      }
    });
  }
}
